import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Button, Box, Paper, Typography, Chip, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, Divider, CircularProgress, Tabs, Tab,
  Select, MenuItem, FormControl, InputLabel, Alert, FormControlLabel, Switch,
} from '@mui/material';
import { ReceiptLong, Receipt, Edit, Block, CalendarToday, Visibility, Print } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate } from 'react-router-dom';
import {
  API_BASE_URL, REGISTROS_LISTAR, REGISTRO_ANULAR, REGISTRO_GET, FACTURACION_GET, FACTURACION_EMITIR,
  REGISTROS_POR_PRODUCTO, REGISTROS_SUCURSALES, TURNO_ACTUAL,
} from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import TituloPagina from './utils/TituloPagina.jsx';
import ExportToolbar from './utils/ExportToolbar.jsx';
import axios from 'axios';
import { imprimirTicketSilencioso } from '../services/impresora';
import { avisarExito, avisarError } from '../services/toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const toYmd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  if (isNaN(d)) return String(f);
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

// Popup de consulta: trae el detalle de una venta y permite pasar a edición.
function RegistroDetalleDialog({ id, onClose, onEditar, impresionGlobal, facturacionHabilitada }) {
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState('');
  const [imprimiendo, setImprimiendo] = useState(false);
  const [avisoImpresion, setAvisoImpresion] = useState('');
  const [facturando, setFacturando] = useState(false);
  const [errorFactura, setErrorFactura] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    axios.get(API_BASE_URL + REGISTRO_GET, { params: { id }, headers: authHeader() })
      .then((res) => setDetalle(res.data?.data ?? null))
      .catch((e) => setError(e.response?.data?.message || 'Error al cargar el detalle.'))
      .finally(() => setLoading(false));
  }, [id]);

  const anulado = detalle && Number(detalle.anulado) === 1;

  const reimprimir = async () => {
    if (!detalle) return;
    setImprimiendo(true);
    setAvisoImpresion('');
    const r = await imprimirTicketSilencioso(
      {
        id: detalle.id, fecha: detalle.fecha, sucursalNombre: detalle.sucursal, sucursalDireccion: detalle.direccion,
        vendedor: detalle.vendedor, items: detalle.items, observaciones: detalle.observaciones,
        subtotal: Number(detalle.total) + Number(detalle.descuento_monto || 0), total: detalle.total,
        descuentoTipo: detalle.descuento_tipo, descuentoValor: detalle.descuento_valor, descuentoMonto: detalle.descuento_monto,
      },
      detalle,
      impresionGlobal,
    );
    setImprimiendo(false);
    if (!r.ok) setAvisoImpresion(r.motivo === 'sin_configurar' ? 'Esta sucursal no tiene una impresora configurada.' : 'No se pudo imprimir la comanda.');
  };

  const facturar = () => {
    setFacturando(true);
    setErrorFactura('');
    axios.post(API_BASE_URL + FACTURACION_EMITIR, { registro_id: id }, { headers: authHeader() })
      .then((res) => {
        if (!res.data?.success) throw new Error(res.data?.message || 'AFIP rechazó el comprobante.');
        avisarExito(`Comprobante emitido — CAE ${res.data.data.cae}`);
        setDetalle((prev) => ({
          ...prev,
          cae: res.data.data.cae,
          fecha_vencimiento_cae: res.data.data.cae_vencimiento,
          numero_comprobante: res.data.data.numero_comprobante,
          tipo_comprobante: res.data.data.tipo_comprobante,
        }));
      })
      .catch((e) => setErrorFactura(e.response?.data?.message || e.message || 'Error al facturar.'))
      .finally(() => setFacturando(false));
  };

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Detalle del registro #{id}
        {anulado && <Chip size="small" color="default" label="Anulada" />}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Typography color="error" variant="body2">{error}</Typography>}
        {avisoImpresion && (
          <Typography variant="body2" sx={{ mb: 1.5, color: 'warning.main' }}>{avisoImpresion}</Typography>
        )}
        {!loading && detalle && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Fecha</Typography>
                <Typography variant="body2">{fmtFecha(detalle.fecha)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Local</Typography>
                <Typography variant="body2">{detalle.sucursal || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Vendedor</Typography>
                <Typography variant="body2">{detalle.vendedor || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Turno</Typography>
                <Typography variant="body2">{detalle.turno_id ? `#${detalle.turno_id}` : '—'}</Typography>
              </Box>
            </Box>
            {detalle.observaciones && (
              <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic' }}>“{detalle.observaciones}”</Typography>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="center">Cant.</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">Peso</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detalle.items ?? []).map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{it.descripcion ?? it.art_descripcion}</TableCell>
                    <TableCell align="center">{it.cantidad}</TableCell>
                    <TableCell align="right">{fmtMoneda(it.precio_unitario)}</TableCell>
                    <TableCell align="right">{Number(it.art_peso) > 0 ? `${(Number(it.cantidad) * Number(it.art_peso)).toFixed(3)} kg` : '—'}</TableCell>
                    <TableCell align="right">{fmtMoneda(it.subtotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const pesoTotalDetalle = (detalle.items ?? []).reduce((acc, it) => acc + Number(it.cantidad || 0) * Number(it.art_peso || 0), 0);
                  return pesoTotalDetalle > 0 ? `Peso total: ${pesoTotalDetalle.toFixed(3)} kg` : '';
                })()}
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                {Number(detalle.descuento_monto) > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Subtotal: {fmtMoneda(Number(detalle.total) + Number(detalle.descuento_monto))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Descuento{detalle.descuento_tipo === 'porcentaje' ? ` (${detalle.descuento_valor}%)` : ''}: -{fmtMoneda(detalle.descuento_monto)}
                    </Typography>
                  </>
                )}
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Total: {fmtMoneda(detalle.total)}</Typography>
              </Box>
            </Box>
            {detalle.cae ? (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                Facturado — CAE {detalle.cae}
                {detalle.fecha_vencimiento_cae ? ` (vence ${fmtFecha(detalle.fecha_vencimiento_cae).split(' ')[0]})` : ''}
              </Alert>
            ) : errorFactura ? (
              <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setErrorFactura('')}>{errorFactura}</Alert>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {!loading && detalle && (
          <Button
            variant="outlined"
            startIcon={imprimiendo ? <CircularProgress size={16} /> : <Print />}
            onClick={reimprimir}
            disabled={imprimiendo || !detalle.impresion_habilitada || !detalle.impresion_nombre_impresora}
          >
            Reimprimir
          </Button>
        )}
        {!anulado && !loading && detalle && facturacionHabilitada && !detalle.cae && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={facturando ? <CircularProgress size={16} /> : <Receipt />}
            onClick={facturar}
            disabled={facturando}
          >
            {facturando ? 'Facturando...' : 'Facturar'}
          </Button>
        )}
        {!anulado && !loading && detalle && (
          <Button variant="contained" startIcon={<Edit />} onClick={() => onEditar(id)}>Editar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function RegistrosProductosList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [registros, setRegistros] = useState([]);
  const [startDate, setStartDate] = useState(new Date()); // por defecto: hoy
  const [endDate, setEndDate] = useState(new Date());
  const [anular, setAnular] = useState(null); // registro a anular
  const [verId, setVerId] = useState(null); // id del registro a mostrar en el popup de detalle
  const [impresionGlobal, setImpresionGlobal] = useState({});
  const [facturacionHabilitada, setFacturacionHabilitada] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState(''); // '' = todas
  const perfil = Number(JSON.parse(sessionStorage.getItem('userData') || '{}')?.perfil);

  // Filtro "solo turno activo": para perfil 1/2, elegir una sucursal
  // puntual y ver únicamente lo vendido en su turno abierto (ignora fechas).
  const [soloTurnoActivo, setSoloTurnoActivo] = useState(false);
  // undefined = todavía no se consultó; null = consultado, sin turno abierto; número = turno_id.
  const [turnoIdActivo, setTurnoIdActivo] = useState(undefined);

  // Vista alternativa: en vez del listado de cada registro, la cantidad
  // (y peso/importe) agrupada por producto — mismo filtro de fecha de arriba.
  const [vista, setVista] = useState('detalle'); // 'detalle' | 'producto'
  const [porProducto, setPorProducto] = useState([]);
  const [loadingProducto, setLoadingProducto] = useState(false);

  useEffect(() => {
    axios.get(API_BASE_URL + FACTURACION_GET, { headers: authHeader() })
      .then((res) => {
        const cfg = res.data?.data ?? {};
        // Solo el certificado QZ Tray es global — nombre de fantasía y pie de
        // ticket son por sucursal (vienen con el registro en REGISTRO_GET).
        setImpresionGlobal({ impresion_qz_cert: cfg.impresion_qz_cert || '' });
        setFacturacionHabilitada(!!Number(cfg.habilitado));
      })
      .catch(() => { /* sin config global: reimprimir queda deshabilitado */ });
  }, []);

  useEffect(() => {
    axios.get(API_BASE_URL + REGISTROS_SUCURSALES, { headers: authHeader() })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setSucursales(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setSucursales([]));
  }, []);

  // El toggle solo tiene sentido con UNA sucursal puntual elegida.
  useEffect(() => {
    if (!sucursalId) setSoloTurnoActivo(false);
  }, [sucursalId]);

  useEffect(() => {
    if (!soloTurnoActivo || !sucursalId) { setTurnoIdActivo(undefined); return; }
    setTurnoIdActivo(undefined);
    axios.get(API_BASE_URL + TURNO_ACTUAL, { params: { sucursal_id: sucursalId }, headers: authHeader() })
      .then((res) => setTurnoIdActivo(res.data?.data?.id ?? null))
      .catch(() => setTurnoIdActivo(null));
  }, [soloTurnoActivo, sucursalId]);

  // Si el toggle está activo pero todavía no sabemos (o no hay) turno_id,
  // no se busca nada — se muestra el mensaje de "sin turno abierto" en vez
  // de traer, por error, el listado sin filtrar.
  const filtroTurnoBloqueado = soloTurnoActivo && !turnoIdActivo;

  const fetchRegistros = useCallback(() => {
    if (filtroTurnoBloqueado) { setRegistros([]); return; }
    setLoading(true);
    axios.get(API_BASE_URL + REGISTROS_LISTAR, {
      params: {
        startDate: toYmd(startDate), endDate: toYmd(endDate), sucursal_id: sucursalId || undefined,
        turno_id: soloTurnoActivo ? turnoIdActivo : undefined,
      },
      headers: authHeader(),
    })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setRegistros(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setRegistros([]))
      .finally(() => setLoading(false));
  }, [startDate, endDate, sucursalId, soloTurnoActivo, turnoIdActivo, filtroTurnoBloqueado]);

  useEffect(() => { fetchRegistros(); }, [fetchRegistros]);

  const fetchPorProducto = useCallback(() => {
    if (filtroTurnoBloqueado) { setPorProducto([]); return; }
    setLoadingProducto(true);
    axios.get(API_BASE_URL + REGISTROS_POR_PRODUCTO, {
      params: {
        startDate: toYmd(startDate), endDate: toYmd(endDate), sucursal_id: sucursalId || undefined,
        turno_id: soloTurnoActivo ? turnoIdActivo : undefined,
      },
      headers: authHeader(),
    })
      .then((res) => {
        const payload = res.data?.data ?? res.data;
        setPorProducto(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setPorProducto([]))
      .finally(() => setLoadingProducto(false));
  }, [startDate, endDate, sucursalId, soloTurnoActivo, turnoIdActivo, filtroTurnoBloqueado]);

  // Se trae solo cuando se está mirando esa vista (evita el pedido si nunca se abre).
  useEffect(() => { if (vista === 'producto') fetchPorProducto(); }, [vista, fetchPorProducto]);

  const buscar = () => (vista === 'producto' ? fetchPorProducto() : fetchRegistros());

  const confirmarAnular = () => {
    if (!anular) return;
    axios.post(API_BASE_URL + REGISTRO_ANULAR, { id: anular.id }, { headers: authHeader() })
      .then((res) => {
        if (!res.data?.success) throw new Error(res.data?.message || 'Error al anular.');
        avisarExito('Registro anulado.');
        setAnular(null);
        fetchRegistros();
      })
      .catch((e) => {
        avisarError(e.response?.data?.message || e.message || 'Error al anular.');
        setAnular(null);
      });
  };

  const totalPeriodo = registros.reduce((a, r) => a + Number(r.total || 0), 0);

  const columns = [
    { field: 'fecha', headerName: 'Fecha y hora', width: 150, valueGetter: (p) => fmtFecha(p.row.fecha) },
    { field: 'sucursal', headerName: 'Local', flex: 1, minWidth: 130 },
    { field: 'turno_id', headerName: 'Turno', width: 90, align: 'center', headerAlign: 'center', valueGetter: (p) => p.row.turno_id ? `#${p.row.turno_id}` : '—' },
    { field: 'vendedor', headerName: 'Vendedor', flex: 1, minWidth: 130 },
    { field: 'items', headerName: 'Ítems', width: 80, type: 'number', align: 'center', headerAlign: 'center' },
    {
      field: 'total', headerName: 'Total', width: 140, align: 'right', headerAlign: 'right',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoneda(p.row.total)}</Typography>,
    },
    {
      field: 'actions', headerName: '', width: 130, sortable: false, filterable: false, align: 'right',
      renderCell: (p) => (
        <>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setVerId(p.row.id); }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/registro-producto?id=${p.row.id}`); }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setAnular(p.row); }}>
            <Block fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const filtros = (
    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      {isMobile ? (
        <>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField size="small" fullWidth InputProps={{
                startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
              }} />
            }
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField size="small" fullWidth InputProps={{
                startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
              }} />
            }
          />
        </>
      ) : (
        <>
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            dateFormat="dd/MM/yyyy"
            customInput={<TextField label="Fecha Desde" size="small" />}
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            dateFormat="dd/MM/yyyy"
            customInput={<TextField label="Fecha Hasta" size="small" />}
          />
        </>
      )}
      {sucursales.length > 0 && (
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select label="Sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {sucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      {(perfil === 1 || perfil === 2) && !!sucursalId && (
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Switch size="small" checked={soloTurnoActivo} onChange={(e) => setSoloTurnoActivo(e.target.checked)} />}
          label="Solo turno activo"
        />
      )}
      <Button onClick={buscar} variant="contained" disabled={soloTurnoActivo && turnoIdActivo === undefined}>
        Buscar
      </Button>
    </Box>
  );

  const columnasPorProducto = [
    { field: 'descripcion', headerName: 'Producto', flex: 1, minWidth: 200 },
    { field: 'cantidad', headerName: 'Cantidad', width: 110, type: 'number', align: 'center', headerAlign: 'center' },
    {
      field: 'peso_kg', headerName: 'Peso', width: 110, align: 'right', headerAlign: 'right',
      renderCell: (p) => Number(p.row.peso_kg) > 0 ? `${Number(p.row.peso_kg).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg` : '—',
    },
    {
      field: 'importe', headerName: 'Importe', width: 140, align: 'right', headerAlign: 'right',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoneda(p.row.importe)}</Typography>,
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={ReceiptLong}
          titulo="Registros de productos"
          subtitulo={`${registros.length} registro${registros.length !== 1 ? 's' : ''} en el período`}
          ayuda="Tocá una fila para ver el detalle, editarla, anularla o reimprimir la comanda. La pestaña 'Por producto' suma cantidades por producto en vez de listar venta por venta."
          accion={
            <Button variant="contained" color="primary" onClick={() => navigate('/registro-producto?mode=create')}>
              Nuevo registro
            </Button>
          }
        />

        {filtros}

        {soloTurnoActivo && turnoIdActivo === null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esa sucursal no tiene un turno abierto ahora mismo.
          </Alert>
        )}

        <Tabs value={vista} onChange={(e, v) => setVista(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="detalle" label="Detalle" />
          <Tab value="producto" label="Por producto" />
        </Tabs>

        {vista === 'detalle' ? (
          <>
            {/* Resumen del período */}
            {!loading && (
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Paper variant="outlined" sx={{ px: 2, py: 1, flex: '1 1 auto', minWidth: 120 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{registros.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Registros</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1, flex: '1 1 auto', minWidth: 120 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'primary.main' }}>{fmtMoneda(totalPeriodo)}</Typography>
                  <Typography variant="caption" color="text.secondary">Total del período</Typography>
                </Paper>
              </Box>
            )}

            {loading ? (
              <Cargando />
            ) : isMobile ? (
              // ---- Vista mobile: cards ----
              <Stack spacing={1.5}>
                {registros.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Sin registros en el período.
                  </Typography>
                )}
                {registros.map((r) => (
                  <Paper key={r.id} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1.5, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                    <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setVerId(r.id)}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{fmtMoneda(r.total)}</Typography>
                        <Chip size="small" label={`${r.items} ítems`} variant="outlined" />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>{r.sucursal || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtFecha(r.fecha)} · {r.vendedor || '—'}{r.turno_id ? ` · Turno #${r.turno_id}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <IconButton size="small" color="primary" onClick={() => navigate(`/registro-producto?id=${r.id}`)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setAnular(r)}>
                        <Block fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            ) : (
              // ---- Vista desktop: DataGrid ----
              <div style={{ height: 560, width: '100%' }}>
                <DataGrid
                  rows={registros} columns={columns} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
                  getRowId={(row) => row.id} disableSelectionOnClick
                  onRowClick={(params) => setVerId(params.row.id)}
                  sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                  components={{ Toolbar: ExportToolbar }}
                />
              </div>
            )}
          </>
        ) : (
          // ---- Vista "Por producto": cantidad/peso/importe agrupado, mismo filtro de fecha ----
          loadingProducto ? (
            <Cargando />
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {porProducto.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  Sin registros en el período.
                </Typography>
              )}
              {porProducto.map((p, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.descripcion}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{p.cantidad}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {Number(p.peso_kg) > 0 ? `${Number(p.peso_kg).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg · ` : ''}
                    {fmtMoneda(p.importe)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <div style={{ height: 560, width: '100%' }}>
              <DataGrid
                rows={porProducto} columns={columnasPorProducto} pageSize={10} rowsPerPageOptions={[10, 25, 50]}
                getRowId={(row) => row.descripcion} disableSelectionOnClick
                components={{ Toolbar: ExportToolbar }}
              />
            </div>
          )
        )}
      </Box>

      <Dialog open={!!anular} onClose={() => setAnular(null)}>
        <DialogTitle>Anular registro</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Anular el registro del {anular ? fmtFecha(anular.fecha) : ''} por {anular ? fmtMoneda(anular.total) : ''}? Esta acción lo oculta del listado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnular(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarAnular}>Anular</Button>
        </DialogActions>
      </Dialog>

      {verId && (
        <RegistroDetalleDialog
          id={verId}
          onClose={() => setVerId(null)}
          onEditar={(id) => navigate(`/registro-producto?id=${id}`)}
          impresionGlobal={impresionGlobal}
          facturacionHabilitada={facturacionHabilitada}
        />
      )}
    </Container>
  );
}

export default RegistrosProductosList;
